import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { 
  Activity, 
  getActivities, 
  getUnreadActivityCount, 
  markActivityAsRead as apiMarkActivityAsRead,
  markAllActivitiesAsRead as apiMarkAllActivitiesAsRead,
  dismissActivity as apiDismissActivity,
  undismissActivity as apiUndismissActivity,
  dismissAllActivities as apiDismissAllActivities
} from '../api/activities';
import {
  getBandmates,
  findOrCreateDM,
  createGroup as apiCreateGroup,
  getConversations as apiGetConversations,
  getMessages,
  sendMessage as apiSendMessage,
  markConversationRead,
  editMessage as apiEditMessage,
  deleteMessage as apiDeleteMessage,
  addReaction as apiAddReaction,
  removeReaction as apiRemoveReaction,
  getAiSuggestions as apiGetAiSuggestions,
  dismissAiSuggestion as apiDismissAiSuggestion,
  acceptAiSuggestion as apiAcceptAiSuggestion,
  triggerAiSuggestion as apiTriggerAiSuggestion,
  AcceptAiSuggestionInput,
} from '../api/chat';
import { incrementUnread, setupFocusListener, isTabFocused } from '../utils/tabNotifications';
import { showChatNotification } from '../utils/osNotifications';
import { DUCKING_EVENT } from '../hooks/useAudioDucking';
import type { ChatMessage, ChatConversationSummary, BandmatesResponse, ChatAiSuggestion } from '../types';

export interface Notification {
  id: string;
  type: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR';
  title: string;
  message: string;
  resourceLink: string | null;
  isRead: boolean;
  createdAt: string;
}

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  // Notifications
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  fetchNotifications: () => Promise<void>;
  // Activities
  activities: Activity[];
  unreadActivityCount: number;
  isLoadingActivities: boolean;
  fetchActivities: () => Promise<void>;
  markActivityAsRead: (activityId: string) => Promise<void>;
  markAllActivitiesAsRead: () => Promise<void>;
  dismissActivity: (activityId: string) => Promise<void>;
  undismissActivity: (activityId: string) => Promise<void>;
  dismissAllActivities: () => Promise<void>;
  // Chat
  bandmates: BandmatesResponse | null;
  totalUnreadChat: number;
  /** Group conversations the current user belongs to, keyed by projectId */
  groupConversations: ChatConversationSummary[];
  /** Messages keyed by conversationId */
  chatMessages: Record<string, ChatMessage[]>;
  /** Whether a conversation has more history to load */
  chatHasMore: Record<string, boolean>;
  /** Typing indicators keyed by conversationId → Set of userIds currently typing */
  typingUsers: Record<string, Set<string>>;
  onlineUsers: Set<string>;
  activeConversationId: string | null;
  /** Metadata for the active conversation (null until loaded) */
  activeConversation: ChatConversationSummary | null;
  /** Map from otherUserId → conversationId for DMs (populated on demand) */
  dmConversationMap: Record<string, string>;
  /** Whether chat drawer is in expanded (full-screen) mode */
  chatExpanded: boolean;
  setActiveConversationId: (conversationId: string | null) => void;
  setChatExpanded: (expanded: boolean) => void;
  fetchBandmates: () => Promise<void>;
  fetchConversations: () => Promise<void>;
  /** Open a DM by otherUserId — finds/creates the DM conversation then opens it */
  openDM: (otherUserId: string) => Promise<void>;
  /** Open a conversation by conversationId directly */
  openConversation: (conversationId: string) => Promise<void>;
  sendChatMessage: (conversationId: string, content: string) => Promise<void>;
  editChatMessage: (conversationId: string, messageId: string, content: string) => Promise<void>;
  deleteChatMessage: (conversationId: string, messageId: string) => Promise<void>;
  addMessageReaction: (conversationId: string, messageId: string, emoji: string) => Promise<void>;
  removeMessageReaction: (conversationId: string, messageId: string, emoji: string) => Promise<void>;
  loadMoreMessages: (conversationId: string) => Promise<void>;
  /** Create a group conversation and open it */
  createGroupChat: (projectId: string, memberIds: string[], name?: string) => Promise<void>;
  /** Set whether the chat input is currently focused */
  setChatInputFocused: (focused: boolean) => void;
  aiSuggestions: Record<string, ChatAiSuggestion[]>;
  fetchAiSuggestions: (conversationId: string) => Promise<void>;
  dismissAiSuggestion: (suggestionId: string) => Promise<void>;
  acceptAiSuggestion: (suggestionId: string, input: AcceptAiSuggestionInput) => Promise<void>;
  triggerAiSuggestion: (messageId: string) => Promise<'CREATED' | 'EXISTING'>;
  aiAcceptedNotices: Record<string, string[]>;
  consumeAiAcceptedNotice: (conversationId: string, text: string) => void;
}

const SocketContext = createContext<SocketContextType | null>(null);

// In development, Vite proxies /api to the backend. For socket.io, we need the direct backend URL.
// In production, socket.io will connect to the same host as the page.
const getSocketUrl = (): string => {
  // In production, connect to the same origin
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  // In development, connect directly to the backend
  return 'http://localhost:3000';
};

const API_BASE = '/api';

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  
  // Notification state
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Activity state
  const [activities, setActivities] = useState<Activity[]>([]);
  const [unreadActivityCount, setUnreadActivityCount] = useState(0);
  const [isLoadingActivities, setIsLoadingActivities] = useState(true);

  // Chat state
  const [bandmates, setBandmates] = useState<BandmatesResponse | null>(null);
  const [totalUnreadChat, setTotalUnreadChat] = useState(0);
  const [groupConversations, setGroupConversations] = useState<ChatConversationSummary[]>([]);
  const [chatMessages, setChatMessages] = useState<Record<string, ChatMessage[]>>({});
  const [chatHasMore, setChatHasMore] = useState<Record<string, boolean>>({});
  const [typingUsers, setTypingUsers] = useState<Record<string, Set<string>>>({});
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [activeConversation, setActiveConversation] = useState<ChatConversationSummary | null>(null);
  const [dmConversationMap, setDmConversationMap] = useState<Record<string, string>>({});
  const [chatExpanded, setChatExpanded] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, ChatAiSuggestion[]>>({});
  const [aiAcceptedNotices, setAiAcceptedNotices] = useState<Record<string, string[]>>({});
  const typingTimeouts = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Chat sounds - volume levels
  const RECEIVE_VOLUME_NORMAL = 0.2;
  const RECEIVE_VOLUME_UNFOCUSED = 0.45;
  const TYPING_VOLUME = 0.1;

  const receiveAudioRef = useRef<HTMLAudioElement | null>(null);
  const typingAudioRef = useRef<HTMLAudioElement | null>(null);
  const chatInputFocusedRef = useRef(false);

  // Initialize sounds and tab notification focus listener on mount
  useEffect(() => {
    // Use old send sound for receive (swapped)
    receiveAudioRef.current = new Audio('/sounds/message-send.mp3');
    receiveAudioRef.current.load();
    receiveAudioRef.current.volume = RECEIVE_VOLUME_NORMAL;

    typingAudioRef.current = new Audio('/sounds/typing.mp3');
    typingAudioRef.current.load();
    typingAudioRef.current.volume = TYPING_VOLUME;

    // Set up focus listener to clear tab indicators when user returns
    const cleanupFocusListener = setupFocusListener();

    return () => {
      receiveAudioRef.current = null;
      typingAudioRef.current = null;
      cleanupFocusListener();
    };
  }, []);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE}/notifications?limit=20`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications);
        setUnreadCount(data.unreadCount);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [isAuthenticated]);

  // Fetch activities from API
  const fetchActivities = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setIsLoadingActivities(true);
      const result = await getActivities({ limit: 50 });
      setActivities(result.activities);
      setUnreadActivityCount(result.unreadCount);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setIsLoadingActivities(false);
    }
  }, [isAuthenticated]);

  // Fetch only unread activity count (for badge updates)
  const fetchUnreadActivityCount = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const count = await getUnreadActivityCount();
      setUnreadActivityCount(count);
    } catch (error) {
      console.error('Failed to fetch unread activity count:', error);
    }
  }, [isAuthenticated]);

  // Mark a single notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE}/notifications/${notificationId}/read`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId ? { ...n, isRead: true } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE}/notifications/read-all`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, []);

  // Mark a single activity as read
  const markActivityAsRead = useCallback(async (activityId: string) => {
    try {
      await apiMarkActivityAsRead(activityId);
      setActivities((prev) =>
        prev.map((a) =>
          a.id === activityId ? { ...a, isRead: true } : a
        )
      );
      setUnreadActivityCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark activity as read:', error);
    }
  }, []);

  // Mark all activities as read
  const markAllActivitiesAsRead = useCallback(async () => {
    try {
      await apiMarkAllActivitiesAsRead();
      setActivities((prev) => prev.map((a) => ({ ...a, isRead: true })));
      setUnreadActivityCount(0);
    } catch (error) {
      console.error('Failed to mark all activities as read:', error);
    }
  }, []);

  // Dismiss an activity (hide from user's feed)
  const dismissActivity = useCallback(async (activityId: string) => {
    let originalActivities: Activity[] = [];
    let wasUnread = false;

    try {
      // Find the activity before removing it (to support undo)
      const activity = activities.find(a => a.id === activityId);
      if (!activity) return;
      
      wasUnread = !activity.isRead;
      originalActivities = [...activities];

      // Optimistically remove from local state
      setActivities((prev) => prev.filter((a) => a.id !== activityId));
      if (wasUnread) {
        setUnreadActivityCount((prev) => Math.max(0, prev - 1));
      }
      
      // Show undo toast
      toast.info('Activity dismissed', {
        action: {
          label: 'Undo',
          onClick: () => undismissActivity(activityId),
        },
      });

      // Call API
      await apiDismissActivity(activityId);
    } catch (error) {
      console.error('Failed to dismiss activity:', error);
      // Rollback on error
      if (originalActivities.length > 0) {
        setActivities(originalActivities);
        if (wasUnread) {
          setUnreadActivityCount((prev) => prev + 1);
        }
      } else {
        fetchActivities();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities, fetchActivities]);

  // Undismiss an activity (restore to feed)
  const undismissActivity = useCallback(async (activityId: string) => {
    try {
      await apiUndismissActivity(activityId);
      // We need to refetch to get the full activity object back in its correct position
      await fetchActivities();
      toast.success('Activity restored');
    } catch (error) {
      console.error('Failed to undismiss activity:', error);
      toast.error('Failed to restore activity');
    }
  }, [fetchActivities]);

  // Dismiss all activities
  const dismissAllActivities = useCallback(async () => {
    try {
      const count = await apiDismissAllActivities();
      if (count > 0) {
        setActivities([]);
        setUnreadActivityCount(0);
        toast.success(`Dismissed ${count} activities`);
      }
    } catch (error) {
      console.error('Failed to dismiss all activities:', error);
      toast.error('Failed to dismiss activities');
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Chat actions
  // ---------------------------------------------------------------------------

  const fetchBandmates = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await getBandmates();
      setBandmates(data);
      setTotalUnreadChat(data.totalUnread);
    } catch (error) {
      console.error('Failed to fetch bandmates:', error);
    }
  }, [isAuthenticated]);

  /**
   * Fetch all conversations (DM + GROUP) for the current user.
   * Stores only GROUP conversations in state (DMs are accessed via bandmates list).
   */
  const fetchConversations = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const conversations = await apiGetConversations();
      const groups = conversations.filter((c) => c.type === 'GROUP');
      setGroupConversations(groups);

      // Recalculate totalUnreadChat from source of truth (DM + group)
      // Use setBandmates callback to read current DM unread without a stale closure
      const groupUnread = groups.reduce((sum, c) => sum + c.unreadCount, 0);
      setBandmates((currentBandmates) => {
        const dmUnread = currentBandmates?.totalUnread ?? 0;
        setTotalUnreadChat(dmUnread + groupUnread);
        return currentBandmates; // unchanged
      });
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  }, [isAuthenticated]);

  /**
   * Open a conversation by its conversationId. Fetches messages and marks read.
   * For group conversations, also sets activeConversation metadata and clears unread.
   */
  const openConversation = useCallback(async (conversationId: string) => {
    setActiveConversationId(conversationId);

    // Look up group conversation to set activeConversation + clear its unread badge
    setGroupConversations((prev) => {
      const group = prev.find((c) => c.id === conversationId);
      if (group) {
        setActiveConversation({ ...group, unreadCount: 0 });
        if (group.unreadCount > 0) {
          setTotalUnreadChat((t) => Math.max(0, t - group.unreadCount));
        }
        return prev.map((c) => c.id === conversationId ? { ...c, unreadCount: 0 } : c);
      }
      return prev;
    });

    try {
      const result = await getMessages(conversationId, { limit: 50 });
      setChatMessages((prev) => ({ ...prev, [conversationId]: result.messages }));
      setChatHasMore((prev) => ({ ...prev, [conversationId]: result.hasMore }));
      const suggestions = await apiGetAiSuggestions(conversationId);
      setAiSuggestions((prev) => ({ ...prev, [conversationId]: suggestions }));
      // Mark as read on the server
      markConversationRead(conversationId).catch(console.error);
    } catch (error) {
      console.error('Failed to open conversation:', error);
    }
  }, []);

  const fetchAiSuggestions = useCallback(async (conversationId: string) => {
    try {
      const suggestions = await apiGetAiSuggestions(conversationId);
      setAiSuggestions((prev) => ({ ...prev, [conversationId]: suggestions }));
    } catch (error) {
      console.error('Failed to fetch AI suggestions:', error);
    }
  }, []);

  const dismissAiSuggestion = useCallback(async (suggestionId: string) => {
    let previousSuggestions: Record<string, ChatAiSuggestion[]> | null = null;

    setAiSuggestions((prev) => {
      previousSuggestions = prev;
      const next: Record<string, ChatAiSuggestion[]> = {};
      for (const [conversationId, suggestions] of Object.entries(prev)) {
        next[conversationId] = suggestions.filter((s) => s.id !== suggestionId);
      }
      return next;
    });

    try {
      await apiDismissAiSuggestion(suggestionId);
    } catch (error) {
      console.error('Failed to dismiss AI suggestion:', error);
      if (previousSuggestions) {
        setAiSuggestions(previousSuggestions);
      }
      toast.error('Failed to dismiss suggestion');
    }
  }, []);

  const acceptAiSuggestion = useCallback(async (suggestionId: string, input: AcceptAiSuggestionInput) => {
    const activeId = activeConversationId;
    if (!activeId) return;

    try {
      await apiAcceptAiSuggestion(suggestionId, input);
      setAiSuggestions((prev) => ({
        ...prev,
        [activeId]: (prev[activeId] ?? []).filter((s) => s.id !== suggestionId),
      }));
    } catch (error) {
      console.error('Failed to accept AI suggestion:', error);
      toast.error('Failed to accept suggestion');
    }
  }, [activeConversationId]);

  const triggerAiSuggestion = useCallback(async (messageId: string): Promise<'CREATED' | 'EXISTING'> => {
    const result = await apiTriggerAiSuggestion(messageId);
    const suggestion = result.suggestion;

    setAiSuggestions((prev) => {
      const existing = prev[suggestion.conversationId] ?? [];
      if (existing.some((s) => s.id === suggestion.id)) {
        return prev;
      }

      const withoutMessagePending = existing.filter(
        (s) => !(s.messageId === suggestion.messageId && s.status === 'PENDING')
      );

      return {
        ...prev,
        [suggestion.conversationId]: [...withoutMessagePending, suggestion],
      };
    });

    return result.status;
  }, []);

  const consumeAiAcceptedNotice = useCallback((conversationId: string, text: string) => {
    setAiAcceptedNotices((prev) => {
      const queue = prev[conversationId] ?? [];
      const index = queue.indexOf(text);
      if (index === -1) return prev;
      const nextQueue = [...queue.slice(0, index), ...queue.slice(index + 1)];
      return {
        ...prev,
        [conversationId]: nextQueue,
      };
    });
  }, []);

  useEffect(() => {
    if (!Object.keys(aiAcceptedNotices).length) return;

    const timer = setTimeout(() => {
      setAiAcceptedNotices((prev) => {
        const next: Record<string, string[]> = {};
        for (const [conversationId, queue] of Object.entries(prev)) {
          next[conversationId] = queue.slice(1);
        }
        return next;
      });
    }, 7000);

    return () => clearTimeout(timer);
  }, [aiAcceptedNotices]);

  /**
   * Open a DM with another user. Calls findOrCreateDM to get the conversationId,
   * caches the mapping, clears the unread badge, then delegates to openConversation.
   */
  const openDM = useCallback(async (otherUserId: string) => {
    try {
      const dm = await findOrCreateDM(otherUserId);
      const convId = dm.conversationId;

      // Cache the userId → conversationId mapping
      setDmConversationMap((prev) => ({ ...prev, [otherUserId]: convId }));

      // Build a lightweight conversation summary for the active header
      setActiveConversation({
        id: convId,
        type: 'DM',
        name: null,
        projectId: null,
        project: null,
        members: dm.other
          ? [{ id: dm.other.id, name: dm.other.name, avatarUrl: dm.other.avatarUrl, role: 'member', isOnline: dm.other.isOnline }]
          : [],
        lastMessage: null,
        unreadCount: 0,
        updatedAt: new Date().toISOString(),
      });

      // Clear unread count in bandmates list for this user
      setBandmates((prev) => {
        if (!prev) return prev;
        let clearedCount = 0;
        const projects = prev.projects.map((p) => ({
          ...p,
          members: p.members.map((m) => {
            if (m.id === otherUserId) {
              clearedCount += m.unreadCount;
              return { ...m, unreadCount: 0 };
            }
            return m;
          }),
        }));
        return {
          ...prev,
          projects,
          totalUnread: Math.max(0, prev.totalUnread - clearedCount),
        };
      });
      setTotalUnreadChat((prev) => {
        const wasUnread = bandmates?.projects
          .flatMap((p) => p.members)
          .find((m) => m.id === otherUserId)?.unreadCount ?? 0;
        return Math.max(0, prev - wasUnread);
      });

      await openConversation(convId);
    } catch (error) {
      console.error('Failed to open DM:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openConversation, bandmates]);

  /**
   * Create a group conversation within a project, then open it in the ChatDrawer.
   */
  const createGroupChat = useCallback(async (projectId: string, memberIds: string[], name?: string) => {
    try {
      const group = await apiCreateGroup(projectId, memberIds, name);

      // Build conversation summary for the active header
      const summary: ChatConversationSummary = {
        id: group.id,
        type: 'GROUP',
        name: group.name,
        projectId: group.projectId,
        project: group.project,
        members: group.members,
        lastMessage: null,
        unreadCount: 0,
        updatedAt: group.updatedAt,
      };

      setActiveConversation(summary);

      // Add to group conversations list
      setGroupConversations((prev) => {
        if (prev.some((c) => c.id === group.id)) return prev;
        return [summary, ...prev];
      });

      await openConversation(group.id);
    } catch (error) {
      console.error('Failed to create group chat:', error);
      throw error;
    }
  }, [openConversation]);

  const sendChatMessage = useCallback(async (conversationId: string, content: string) => {
    try {
      const message = await apiSendMessage(conversationId, content);
      setChatMessages((prev) => ({
        ...prev,
        [conversationId]: [...(prev[conversationId] ?? []), message],
      }));

      // Suggestion creation is async on the backend and may arrive after
      // the message response. Refresh once to avoid missing a socket event.
      setTimeout(() => {
        fetchAiSuggestions(conversationId).catch((error) => {
          console.error('Failed to refresh AI suggestions after send:', error);
        });
      }, 900);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    }
  }, [fetchAiSuggestions]);

  const editChatMessage = useCallback(async (conversationId: string, messageId: string, content: string) => {
    try {
      const updated = await apiEditMessage(messageId, content);
      setChatMessages((prev) => ({
        ...prev,
        [conversationId]: (prev[conversationId] ?? []).map((m) =>
          m.id === messageId ? { ...m, content: updated.content, editedAt: updated.editedAt } : m
        ),
      }));
    } catch (error) {
      console.error('Failed to edit message:', error);
      toast.error('Failed to edit message');
    }
  }, []);

  const deleteChatMessage = useCallback(async (conversationId: string, messageId: string) => {
    try {
      await apiDeleteMessage(messageId);
      // Soft delete - mark as deleted instead of removing
      setChatMessages((prev) => ({
        ...prev,
        [conversationId]: (prev[conversationId] ?? []).map((m) =>
          m.id === messageId ? { ...m, deletedAt: new Date().toISOString() } : m
        ),
      }));
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error('Failed to delete message');
    }
  }, []);

  const addMessageReaction = useCallback(async (conversationId: string, messageId: string, emoji: string) => {
    try {
      // Optimistic update
      setChatMessages((prev) => ({
        ...prev,
        [conversationId]: (prev[conversationId] ?? []).map((m) => {
          if (m.id !== messageId) return m;
          const reactions = m.reactions || [];
          const existingReaction = reactions.find((r) => r.emoji === emoji);
          
          if (existingReaction) {
            if (existingReaction.userReacted) return m; // Already reacted
            return {
              ...m,
              reactions: reactions.map((r) =>
                r.emoji === emoji
                  ? { ...r, count: r.count + 1, userReacted: true, users: [...r.users, { id: user!.id, name: user!.name, avatarUrl: user!.avatarUrl ?? null }] }
                  : r
              ),
            };
          } else {
            return {
              ...m,
              reactions: [...reactions, { emoji, count: 1, userReacted: true, users: [{ id: user!.id, name: user!.name, avatarUrl: user!.avatarUrl ?? null }] }],
            };
          }
        }),
      }));

      await apiAddReaction(messageId, emoji);
    } catch (error) {
      console.error('Failed to add reaction:', error);
      toast.error('Failed to add reaction');
      // Revert optimistic update on error
      // TODO: Implement revert logic if needed
    }
  }, [user]);

  const removeMessageReaction = useCallback(async (conversationId: string, messageId: string, emoji: string) => {
    try {
      // Optimistic update
      setChatMessages((prev) => ({
        ...prev,
        [conversationId]: (prev[conversationId] ?? []).map((m) => {
          if (m.id !== messageId) return m;
          const reactions = m.reactions || [];
          const existingReaction = reactions.find((r) => r.emoji === emoji);
          
          if (!existingReaction || !existingReaction.userReacted) return m;
          
          const updatedCount = existingReaction.count - 1;
          if (updatedCount <= 0) {
            return { ...m, reactions: reactions.filter((r) => r.emoji !== emoji) };
          } else {
            return {
              ...m,
              reactions: reactions.map((r) =>
                r.emoji === emoji
                  ? { ...r, count: updatedCount, userReacted: false, users: r.users.filter((u) => u.id !== user!.id) }
                  : r
              ),
            };
          }
        }),
      }));

      await apiRemoveReaction(messageId, emoji);
    } catch (error) {
      console.error('Failed to remove reaction:', error);
      toast.error('Failed to remove reaction');
      // Revert optimistic update on error
      // TODO: Implement revert logic if needed
    }
  }, [user]);

  const loadMoreMessages = useCallback(async (conversationId: string) => {
    const existing = chatMessages[conversationId];
    if (!existing?.length) return;
    const oldest = existing[0];
    try {
      const result = await getMessages(conversationId, { limit: 50, before: oldest.createdAt });
      setChatMessages((prev) => ({
        ...prev,
        [conversationId]: [...result.messages, ...(prev[conversationId] ?? [])],
      }));
      setChatHasMore((prev) => ({ ...prev, [conversationId]: result.hasMore }));
    } catch (error) {
      console.error('Failed to load more messages:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMessages]);

  const setChatInputFocused = useCallback((focused: boolean) => {
    chatInputFocusedRef.current = focused;
  }, []);

  // Initialize socket connection when authenticated
  useEffect(() => {
    if (!isAuthenticated || !user) {
      // Disconnect if not authenticated
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      // Clear state
      setActivities([]);
      setUnreadActivityCount(0);
      setIsLoadingActivities(true);
      // Clear chat state
      setBandmates(null);
      setTotalUnreadChat(0);
      setGroupConversations([]);
      setChatMessages({});
      setChatHasMore({});
      setTypingUsers({});
      setOnlineUsers(new Set());
      setActiveConversationId(null);
      setActiveConversation(null);
      setDmConversationMap({});
      return;
    }

    // Avoid reconnecting if already connected with same user
    if (socket?.connected) {
      return;
    }

    // Get token from localStorage if available (for regular login)
    // OAuth users may not have token in localStorage - they use httpOnly cookies
    const token = localStorage.getItem('accessToken');

    // Create socket connection
    const socketUrl = getSocketUrl();
    const newSocket = io(socketUrl, {
      // Pass token if available, otherwise socket.io will use cookies via withCredentials
      auth: token ? { token } : {},
      transports: ['websocket', 'polling'],
      withCredentials: true, // Enable sending cookies for OAuth users
    });

    newSocket.on('connect', () => {
      console.log('[Socket] Connected');
      setIsConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('[Socket] Disconnected');
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('[Socket] Connection error:', error.message);
      setIsConnected(false);
    });

    // Listen for real-time notifications
    newSocket.on('notification', (notification: Notification) => {
      console.log('[Socket] Received notification:', notification);

      // Add to local state
      setNotifications((prev) => [notification, ...prev]);
      setUnreadCount((prev) => prev + 1);

      // No pop-up toast here; rely on bell badge/dropdown and activity feed.
    });

    // Listen for real-time activities
    newSocket.on('activity', (activity: Activity) => {
      console.log('[Socket] Received activity:', activity);

      // Prepend new activity to the list
      setActivities((prev) => {
        // Avoid duplicates
        if (prev.some(a => a.id === activity.id)) {
          return prev;
        }
        // Mark as read if it's from the current user, unread if from others
        const isOwnAction = activity.userId === user.id;
        return [{ ...activity, isRead: isOwnAction }, ...prev.slice(0, 49)];
      });
      
      // Only increment unread count if it's from another user
      if (activity.userId !== user.id) {
        setUnreadActivityCount((prev) => prev + 1);
      }
    });

    // -----------------------------------------------------------------------
    // Chat socket events
    // -----------------------------------------------------------------------

    // Incoming message — keyed by conversationId
    newSocket.on('chat:message', (message: ChatMessage) => {
      const convId = message.conversationId;
      console.log('[Socket] Received chat message in conversation:', convId);

      // Play receive sound and update tab indicators for messages from others
      const isFromOther = message.senderId !== user?.id;
      if (isFromOther) {
        // Play sound - louder when tab not focused
        if (receiveAudioRef.current) {
          receiveAudioRef.current.volume = isTabFocused() 
            ? RECEIVE_VOLUME_NORMAL 
            : RECEIVE_VOLUME_UNFOCUSED;
          receiveAudioRef.current.currentTime = 0;
          receiveAudioRef.current.play().catch((err) => {
            console.debug('Could not play receive sound:', err.message);
          });
        }
        // Duck active audio playback (skip if chat is focused)
        if (!chatInputFocusedRef.current) {
          window.dispatchEvent(new Event(DUCKING_EVENT));
        }

        // Update tab title and favicon badge
        incrementUnread();

        // Show OS notification if tab not focused
        // Determine if this is a DM by checking group conversations
        const groupConv = groupConversations.find(g => g.id === convId);
        const isDM = !groupConv; // If not in group conversations, it's a DM
        
        showChatNotification({
          senderName: message.sender.name,
          message: message.content,
          avatarUrl: message.sender.avatarUrl || '/logo.png', // Fall back to BandMate logo if no avatar
          conversationId: convId,
          isDM,
          onClickCallback: () => {
            // Open the conversation when notification is clicked
            setActiveConversationId(convId);
          },
        });
      }

      setChatMessages((prev) => {
        const existing = prev[convId] ?? [];
        if (existing.some((m) => m.id === message.id)) return prev;
        return { ...prev, [convId]: [...existing, message] };
      });

      // Update unread count if this conversation is not active
      setActiveConversationId((currentActive) => {
        if (currentActive !== convId) {
          // Determine if this is a group conversation and route unread accordingly
          setGroupConversations((prevGroups) => {
            const groupIdx = prevGroups.findIndex((c) => c.id === convId);

            if (groupIdx !== -1) {
              // GROUP message — increment group unread only
              const updated = [...prevGroups];
              updated[groupIdx] = { ...updated[groupIdx], unreadCount: updated[groupIdx].unreadCount + 1 };
              setTotalUnreadChat((t) => t + 1);
              return updated;
            }

            // DM message — increment bandmates unread
            setBandmates((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                projects: prev.projects.map((p) => ({
                  ...p,
                  members: p.members.map((m) =>
                    m.id === message.senderId ? { ...m, unreadCount: (m.unreadCount ?? 0) + 1 } : m
                  ),
                })),
                totalUnread: prev.totalUnread + 1,
              };
            });
            setTotalUnreadChat((t) => t + 1);
            return prevGroups;
          });
        } else {
          // Conversation is active — mark read immediately
          markConversationRead(convId).catch(console.error);
        }
        return currentActive;
      });
    });

    // Typing indicator — now includes conversationId
    newSocket.on('chat:typing', (data: { conversationId: string; fromUserId: string; isTyping: boolean }) => {
      const key = `${data.conversationId}:${data.fromUserId}`;
      
      // Play typing sound when someone starts typing (not for ourselves) and only if input is focused
      // and the typing is in the active conversation
      if (
        data.isTyping && 
        data.fromUserId !== user?.id && 
        data.conversationId === activeConversationId &&
        chatInputFocusedRef.current && 
        typingAudioRef.current
      ) {
        typingAudioRef.current.currentTime = 0;
        typingAudioRef.current.play().catch((err) => {
          console.debug('Could not play typing sound:', err.message);
        });
      }

      setTypingUsers((prev) => {
        const convTyping = new Set(prev[data.conversationId] ?? []);
        if (data.isTyping) {
          convTyping.add(data.fromUserId);
        } else {
          convTyping.delete(data.fromUserId);
        }
        return { ...prev, [data.conversationId]: convTyping };
      });
      // Auto-clear after 3 s in case the off event is missed
      if (data.isTyping) {
        clearTimeout(typingTimeouts.current[key]);
        typingTimeouts.current[key] = setTimeout(() => {
          setTypingUsers((prev) => {
            const convTyping = new Set(prev[data.conversationId] ?? []);
            convTyping.delete(data.fromUserId);
            return { ...prev, [data.conversationId]: convTyping };
          });
        }, 3000);
      }
    });

    // Presence updates from project room-mates
    newSocket.on('chat:presence', (data: { userId: string; isOnline: boolean }) => {
      setOnlineUsers((prev) => {
        const next = new Set(prev);
        if (data.isOnline) next.add(data.userId);
        else next.delete(data.userId);
        return next;
      });
      // Mirror in bandmates list
      setBandmates((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          projects: prev.projects.map((p) => ({
            ...p,
            members: p.members.map((m) =>
              m.id === data.userId ? { ...m, isOnline: data.isOnline } : m
            ),
          })),
        };
      });
    });

    // Read receipt — someone in the conversation read our messages
    newSocket.on('chat:read', (data: { conversationId: string; readBy: string }) => {
      setChatMessages((prev) => {
        const msgs = prev[data.conversationId];
        if (!msgs) return prev;
        return {
          ...prev,
          [data.conversationId]: msgs.map((m) =>
            m.senderId === user.id && !m.readAt
              ? { ...m, readAt: new Date().toISOString() }
              : m
          ),
        };
      });
    });

    // Message edited by another member
    newSocket.on('chat:messageEdited', (data: { id: string; conversationId: string; content: string; senderId: string; editedAt: string }) => {
      setChatMessages((prev) => {
        const msgs = prev[data.conversationId];
        if (!msgs) return prev;
        return {
          ...prev,
          [data.conversationId]: msgs.map((m) =>
            m.id === data.id ? { ...m, content: data.content, editedAt: data.editedAt } : m
          ),
        };
      });
    });

    // Message deleted by another member (soft delete - mark as deleted, don't remove)
    newSocket.on('chat:messageDeleted', (data: { id: string; conversationId: string }) => {
      setChatMessages((prev) => {
        const msgs = prev[data.conversationId];
        if (!msgs) return prev;
        return {
          ...prev,
          [data.conversationId]: msgs.map((m) =>
            m.id === data.id ? { ...m, deletedAt: new Date().toISOString() } : m
          ),
        };
      });
    });

    // Reaction added to a message
    newSocket.on('chat:reactionAdded', (data: { messageId: string; conversationId: string; emoji: string; userId: string; user: { id: string; name: string; avatarUrl: string | null } }) => {
      setChatMessages((prev) => {
        const msgs = prev[data.conversationId];
        if (!msgs) return prev;
        return {
          ...prev,
          [data.conversationId]: msgs.map((m) => {
            if (m.id !== data.messageId) return m;
            
            const reactions = m.reactions || [];
            const existingReaction = reactions.find((r) => r.emoji === data.emoji);
            
            if (existingReaction) {
              // Increment count and add user if not already there
              const userAlreadyReacted = existingReaction.users.some((u) => u.id === data.userId);
              if (userAlreadyReacted) return m; // No change needed
              
              return {
                ...m,
                reactions: reactions.map((r) =>
                  r.emoji === data.emoji
                    ? {
                        ...r,
                        count: r.count + 1,
                        users: [...r.users, data.user],
                        userReacted: r.userReacted || data.userId === user?.id,
                      }
                    : r
                ),
              };
            } else {
              // Add new reaction
              return {
                ...m,
                reactions: [
                  ...reactions,
                  {
                    emoji: data.emoji,
                    count: 1,
                    users: [data.user],
                    userReacted: data.userId === user?.id,
                  },
                ],
              };
            }
          }),
        };
      });
    });

    // Reaction removed from a message
    newSocket.on('chat:reactionRemoved', (data: { messageId: string; conversationId: string; emoji: string; userId: string }) => {
      setChatMessages((prev) => {
        const msgs = prev[data.conversationId];
        if (!msgs) return prev;
        return {
          ...prev,
          [data.conversationId]: msgs.map((m) => {
            if (m.id !== data.messageId) return m;
            
            const reactions = m.reactions || [];
            const reactionIndex = reactions.findIndex((r) => r.emoji === data.emoji);
            
            if (reactionIndex === -1) return m; // Reaction not found
            
            const reaction = reactions[reactionIndex];
            const updatedCount = reaction.count - 1;
            
            if (updatedCount <= 0) {
              // Remove reaction entirely
              return {
                ...m,
                reactions: reactions.filter((r) => r.emoji !== data.emoji),
              };
            } else {
              // Decrement count and remove user
              return {
                ...m,
                reactions: reactions.map((r) =>
                  r.emoji === data.emoji
                    ? {
                        ...r,
                        count: updatedCount,
                        users: r.users.filter((u) => u.id !== data.userId),
                        userReacted: r.userReacted && data.userId !== user?.id,
                      }
                    : r
                ),
              };
            }
          }),
        };
      });
    });

    newSocket.on('chat:aiSuggestionCreated', (suggestion: ChatAiSuggestion) => {
      setAiSuggestions((prev) => {
        const existing = prev[suggestion.conversationId] ?? [];
        if (existing.some((s) => s.id === suggestion.id)) {
          return prev;
        }
        return {
          ...prev,
          [suggestion.conversationId]: [...existing, suggestion],
        };
      });
    });

    newSocket.on('chat:aiSuggestionUpdated', (data: { id: string; status: string }) => {
      setAiSuggestions((prev) => {
        const next: Record<string, ChatAiSuggestion[]> = {};
        for (const [conversationId, suggestions] of Object.entries(prev)) {
          next[conversationId] = suggestions.filter((s) => s.id !== data.id);
        }
        return next;
      });
    });

    newSocket.on('chat:aiSuggestionAcceptedNotice', (data: { conversationId: string; text: string }) => {
      if (!data?.conversationId || !data?.text) return;
      setAiAcceptedNotices((prev) => ({
        ...prev,
        [data.conversationId]: [...(prev[data.conversationId] ?? []), data.text],
      }));
    });

    // A new group conversation was created that includes us
    newSocket.on('chat:conversationCreated', (conv: ChatConversationSummary) => {
      console.log('[Socket] New conversation created:', conv.id, conv.type);
      if (conv.type === 'GROUP') {
        setGroupConversations((prev) => {
          if (prev.some((c) => c.id === conv.id)) return prev;
          return [conv, ...prev];
        });
      }
    });

    // Conversation metadata updated (e.g. renamed)
    newSocket.on('chat:conversationUpdated', (data: { conversationId: string; name: string }) => {
      setActiveConversation((prev) =>
        prev?.id === data.conversationId ? { ...prev, name: data.name } : prev
      );
      setGroupConversations((prev) =>
        prev.map((c) => c.id === data.conversationId ? { ...c, name: data.name } : c)
      );
    });

    // Members added to a conversation
    newSocket.on('chat:membersAdded', (data: { conversationId: string; members: ChatConversationSummary['members']; addedBy: string }) => {
      setActiveConversation((prev) => {
        if (prev?.id !== data.conversationId) return prev;
        const existingIds = new Set(prev.members.map((m) => m.id));
        const newMembers = data.members.filter((m) => !existingIds.has(m.id));
        return { ...prev, members: [...prev.members, ...newMembers] };
      });
      setGroupConversations((prev) =>
        prev.map((c) => {
          if (c.id !== data.conversationId) return c;
          const existingIds = new Set(c.members.map((m) => m.id));
          const newMembers = data.members.filter((m) => !existingIds.has(m.id));
          return { ...c, members: [...c.members, ...newMembers] };
        })
      );
    });

    // A member was removed from a conversation
    newSocket.on('chat:memberRemoved', (data: { conversationId: string; memberId: string; removedBy: string }) => {
      // If we were removed, close the conversation and remove from list
      if (data.memberId === user.id) {
        setActiveConversationId((current) => current === data.conversationId ? null : current);
        setActiveConversation((prev) => prev?.id === data.conversationId ? null : prev);
        setGroupConversations((prev) => prev.filter((c) => c.id !== data.conversationId));
      } else {
        setActiveConversation((prev) => {
          if (prev?.id !== data.conversationId) return prev;
          return { ...prev, members: prev.members.filter((m) => m.id !== data.memberId) };
        });
        setGroupConversations((prev) =>
          prev.map((c) => {
            if (c.id !== data.conversationId) return c;
            return { ...c, members: c.members.filter((m) => m.id !== data.memberId) };
          })
        );
      }
    });

    setSocket(newSocket);

    // Fetch existing notifications and activities
    fetchNotifications();
    fetchActivities();
    fetchBandmates();
    fetchConversations();

    // Cleanup on unmount
    return () => {
      newSocket.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);

  // Periodically refresh unread count (backup for missed socket events)
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      fetchUnreadActivityCount();
    }, 60000); // Every minute

    return () => clearInterval(interval);
  }, [isAuthenticated, fetchUnreadActivityCount]);

  const value: SocketContextType = {
    socket,
    isConnected,
    // Notifications
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    fetchNotifications,
    // Activities
    activities,
    unreadActivityCount,
    isLoadingActivities,
    fetchActivities,
    markActivityAsRead,
    markAllActivitiesAsRead,
    dismissActivity,
    undismissActivity,
    dismissAllActivities,
    // Chat
    bandmates,
    totalUnreadChat,
    groupConversations,
    chatMessages,
    chatHasMore,
    typingUsers,
    onlineUsers,
    activeConversationId,
    activeConversation,
    dmConversationMap,
    chatExpanded,
    setActiveConversationId,
    setChatExpanded,
    fetchBandmates,
    fetchConversations,
    openDM,
    openConversation,
    sendChatMessage,
    editChatMessage,
    deleteChatMessage,
    addMessageReaction,
    removeMessageReaction,
    loadMoreMessages,
    createGroupChat,
    setChatInputFocused,
    aiSuggestions,
    fetchAiSuggestions,
    dismissAiSuggestion,
    acceptAiSuggestion,
    triggerAiSuggestion,
    aiAcceptedNotices,
    consumeAiAcceptedNotice,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
}

export function useSocket(): SocketContextType {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}
